<template>
	<Modal
		class="parallel-view-modal"
		:title="title"
		:closeable="false"
		:blocking="true"
	>
		<!-- body -->
		<template #body>
			<p>
				{{ $I18n.trans('view_mode.parallel_views.message') }}
			</p>
			<div
				class="parallel-view-modal__device"
			>
				<strong>{{ deviceLabel }}</strong>
			</div>
			<p class="parallel-view-modal__hint">
				{{ $I18n.trans('view_mode.parallel_views.hint') }}
			</p>
			<p
				v-if="error"
				class="parallel-view-modal__error"
				role="alert"
			>
				{{ error }}
			</p>
		</template>
		<!-- footer -->
		<template #footer-buttons>
			<button
				ref="retryButton"
				type="button"
				class="button button--secondary parallel-view-modal__action"
				:disabled="isResuming"
				@click="reload"
			>
				{{ $I18n.trans('view_mode.parallel_views.retry') }}
			</button>
			<button
				ref="resumeButton"
				type="button"
				class="button button--primary parallel-view-modal__action"
				:disabled="isResuming"
				@click="resumeHere"
			>
				{{ resumeLabel }}
			</button>
		</template>
	</Modal>
</template>

<script>
import { mapActions } from 'vuex';
import UAParser from 'ua-parser-js';
import api from '@videotools/js/api/client';
import storage from '@shared/js/storage';
import { bus, EVENT } from '@videotools/js/events';
import Modal from '../modal';

export default {
	name:       'ParallelViewModal',
	components: { Modal },
	props:      {
		parallelView: {
			type:    Object,
			default: () => null,
		},
		eventId: {
			type:     Number,
			required: true,
		},
	},
	data() {
		return {
			title:      this.$I18n.trans('view_mode.parallel_views.title'),
			isResuming: false,
			error:      null,
		};
	},
	computed: {
		deviceLabel() {
			const userAgent = this.parallelView?.userAgent || '';
			if (!userAgent) {
				return this.$I18n.trans('view_mode.parallel_views.unknown_device');
			}
			if (userAgent.includes('Swisscom') || userAgent.includes('BlueTv')) {
				return 'Blue TV';
			}

			const parsed = new UAParser(userAgent).getResult();
			return [parsed.browser.name, parsed.os.name].filter(Boolean).join(' · ') || this.$I18n.trans('view_mode.parallel_views.unknown_device');
		},
		resumeLabel() {
			return this.$I18n.trans(`view_mode.parallel_views.${this.isResuming ? 'resuming' : 'resume'}`);
		},
	},
	mounted() {
		if (!this.$Helpers.isSmartTV()) {
			return;
		}

		// Capture runs before the player hotkeys, which otherwise use the arrows to seek and change volume.
		this.tvKeyListener = (event) => this.handleTvKeyDown(event);
		this.tvKeyTargets = [];
		this.addTvKeyListener(document);
		try {
			if (typeof parent !== 'undefined' && document !== parent.document) {
				this.addTvKeyListener(parent.document);
			}
		} catch {
			// A cross-origin embed parent cannot be reached.
		}
		this.$nextTick(() => this.focusActionButton(this.$refs.resumeButton));
	},
	beforeDestroy() {
		(this.tvKeyTargets || []).forEach((target) => {
			target.removeEventListener('keydown', this.tvKeyListener, true);
		});
	},
	methods: {
		...mapActions({
			setParallelView: 'error/setParallelView',
			setPlayoutError: 'error/setPlayoutError',
		}),
		addTvKeyListener(target) {
			target.addEventListener('keydown', this.tvKeyListener, true);
			this.tvKeyTargets.push(target);
		},
		focusActionButton(button) {
			if (!button || button.disabled) {
				return;
			}
			button.focus();
		},
		handleTvKeyDown(event) {
			const key = event.key?.toUpperCase();
			// Back still leaves the event. Every other remote key stays inside the modal.
			if (!key || key === 'GOBACK') {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const buttons = [this.$refs.retryButton, this.$refs.resumeButton].filter((button) => button && !button.disabled);
			if (buttons.length === 0) {
				return;
			}

			const activeIndex = buttons.findIndex((button) => button.ownerDocument.activeElement === button);
			const currentIndex = activeIndex === -1 ? buttons.length - 1 : activeIndex;

			if (key === 'ARROWLEFT' || key === 'ARROWUP') {
				const previousIndex = (currentIndex - 1 + buttons.length) % buttons.length;
				buttons[previousIndex].focus();
				return;
			}

			if (key === 'ARROWRIGHT' || key === 'ARROWDOWN') {
				const nextIndex = (currentIndex + 1) % buttons.length;
				buttons[nextIndex].focus();
				return;
			}

			if (key === 'ENTER' || key === ' ' || key === 'UNIDENTIFIED') {
				buttons[currentIndex].click();
			}
		},
		reload() {
			window.location.reload();
		},
		isInactiveProtectionError(error) {
			return error?.status === 403 && error?.data?.reason === 'parallelViewInactive';
		},
		// Protection is off, so there is no other session to take over. Keep the current playback.
		continuePlayback() {
			this.setParallelView(null);
			this.setPlayoutError(null);
			bus.publish(EVENT.PLAYER.REQUEST.PLAY);
		},
		async resumeHere() {
			this.isResuming = true;
			this.error = null;
			try {
				await api.resumeParallelView(this.eventId, storage.viewerId);
				this.reload();
			} catch (error) {
				if (this.isInactiveProtectionError(error)) {
					this.continuePlayback();
					return;
				}
				this.isResuming = false;
				this.error = this.$I18n.trans('view_mode.parallel_views.resume_error');
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.parallel-view-modal__device {
	margin: 24px 0;
	border: 1px solid var(--color-grey-750);
	background-color: var(--color-grey-950);
	padding: 16px;
}

.parallel-view-modal__hint {
	margin-bottom: $sp4;
}

.parallel-view-modal__error {
	color: var(--color-red);
}

.parallel-view-modal__action:focus {
	background-color: var(--color-white);
	border-color: var(--color-white);
	color: var(--color-grey-950);
}
</style>
